<template>
  <li class="card">
    <div class="card__top">
      <p class="card__title">{{ item.question }}</p>
      <button
        type="button"
        class="card__expand"
        :class="{ 'card__expand--open': isOpen }"
        @click="toggle"
        aria-label="Expand answer"
      >
        <svg class="card__expand-icon">
          <use xlink:href="@/assets/images/sprites.svg#plus"></use>
        </svg>
      </button>
    </div>

    <div class="card__answer-block">
      <span class="card__quote"></span>
      <p class="card__answer" :class="{ 'card__answer--open': isOpen }">{{ shownAnswer }}</p>
    </div>

    <div class="card__bottom">
      <span class="card__tag">{{ item.category }}</span>
      <ShareButton :url="item.url" />
    </div>
  </li>
</template>

<script>
import { ref, computed } from 'vue'
import ShareButton from '@/components/ui/ShareButton.vue'
import { stripMarkdown } from '@/hooks/useStripMarkdown'

const apiUrl = process.env.VUE_APP_API_URL

export default {
  name: 'AnswerCard',
  components: { ShareButton },
  props: {
    item: Object,
    index: Number,
  },
  setup(props) {
    const isOpen = ref(false)
    const fullAnswer = ref('') // the complete answer, fetched on first expand
    const loading = ref(false)

    // The gallery endpoint only sends a ~200-char slice; use it for the preview.
    const preview = computed(() => stripMarkdown(props.item.answer || ''))

    // Collapsed → the preview (CSS clamps it to 3 lines + "…").
    // Expanded → the full answer once fetched, else the preview meanwhile.
    const shownAnswer = computed(() =>
      isOpen.value && fullAnswer.value ? fullAnswer.value : preview.value
    )

    async function loadFullAnswer() {
      if (fullAnswer.value || loading.value || !props.item.url) return
      try {
        loading.value = true
        const res = await fetch(`${apiUrl}/Question/ByUrl/${props.item.url}`)
        if (res.ok) {
          const data = await res.json()
          fullAnswer.value = stripMarkdown(data.answer || '')
        }
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    function toggle() {
      isOpen.value = !isOpen.value
      if (isOpen.value) loadFullAnswer()
    }

    return { isOpen, shownAnswer, toggle }
  },
}
</script>

<style scoped lang="scss">
.card {
  list-style: none;
  background-color: #1d1f20;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);

  &::marker {
    content: none;
  }

  &__top {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  &__title {
    flex: 1;
    color: var(--white-color, #ffffff);
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__expand {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 50%;
    background-color: #224efe;
    cursor: pointer;
    transition: 0.3s opacity ease-in-out;

    &:hover {
      opacity: 0.85;
    }

    &-icon {
      width: 14px;
      height: 14px;
      fill: #ffffff;
      transition: transform 0.3s ease;
    }

    &--open .card__expand-icon {
      transform: rotate(45deg);
    }
  }

  &__answer-block {
    position: relative;
    margin-top: 20px;
    padding-left: 18px;
  }

  &__quote {
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    border-radius: 2px;
    background-color: rgba(255, 255, 255, 0.2);
  }

  /* Collapsed: clamp to 3 lines with a trailing "…" (clean, never a hard
     mid-word cut). Expanded: show the full answer with paragraph breaks. */
  &__answer {
    color: rgba(255, 255, 255, 0.5);
    font-size: 16px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__answer--open {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
    white-space: pre-line;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 20px;

    :deep(.share) {
      font-size: 16px;
    }
    :deep(.share__icon) {
      width: 36px;
      height: 36px;
    }
  }

  &__tag {
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    padding: 6px 16px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
  }
}

@media screen and (max-width: 560px) {
  .card {
    padding: 16px;

    &__title {
      font-size: 16px;
    }
    &__expand {
      width: 34px;
      height: 34px;
    }
    &__tag {
      padding: 5px 13px;
      font-size: 13px;
    }
    &__bottom :deep(.share) {
      font-size: 14px;
    }
    &__bottom :deep(.share__icon) {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
